import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import {
  Alert, Badge, Button, Group, Loader, Paper, SegmentedControl, Stack, Switch, Table, Textarea, TextInput, Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications as notify } from '@mantine/notifications';
import { IconAlertTriangle, IconBrandWhatsapp, IconMessage, IconSend } from '@tabler/icons-react';
import { isAxiosError } from 'axios';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import {
  useNotificationStatus, useNotificationLog, useSendNotification,
  useNotificationSettings, useUpdateNotificationSettings,
} from './notificationsApi';

const fmtDate = (iso: string) =>
  new Intl.DateTimeFormat('ar-PS', { dateStyle: 'medium', timeStyle: 'short', numberingSystem: 'latn' }).format(new Date(iso));

export function NotificationsPage() {
  const { t } = useTranslation();
  const status = useNotificationStatus();
  const log = useNotificationLog();
  const send = useSendNotification();
  const settings = useNotificationSettings();
  const updateSettings = useUpdateNotificationSettings();

  const [channel, setChannel] = useState('1'); // 1 SMS, 2 WhatsApp
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');

  const settingsForm = useForm({
    initialValues: {
      orderPaidEnabled: false, orderPaidChannel: '2', orderPaidTemplate: '',
      loyaltyEarnEnabled: false, loyaltyEarnChannel: '2', loyaltyEarnTemplate: '',
      lowStockEnabled: false, lowStockChannel: '2', lowStockPhone: '', lowStockTemplate: '',
      smsSenderId: '',
    },
  });
  useEffect(() => {
    if (settings.data) {
      const s = settings.data;
      settingsForm.setValues({
        orderPaidEnabled: s.orderPaidEnabled,
        orderPaidChannel: String(s.orderPaidChannel),
        orderPaidTemplate: s.orderPaidTemplate ?? '',
        loyaltyEarnEnabled: s.loyaltyEarnEnabled,
        loyaltyEarnChannel: String(s.loyaltyEarnChannel),
        loyaltyEarnTemplate: s.loyaltyEarnTemplate ?? '',
        lowStockEnabled: s.lowStockEnabled,
        lowStockChannel: String(s.lowStockChannel),
        lowStockPhone: s.lowStockPhone ?? '',
        lowStockTemplate: s.lowStockTemplate ?? '',
        smsSenderId: s.smsSenderId ?? '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.data]);

  const saveSettings = settingsForm.onSubmit(async (v) => {
    try {
      await updateSettings.mutateAsync({
        orderPaidEnabled: v.orderPaidEnabled,
        orderPaidChannel: Number(v.orderPaidChannel),
        orderPaidTemplate: v.orderPaidTemplate.trim() || null,
        loyaltyEarnEnabled: v.loyaltyEarnEnabled,
        loyaltyEarnChannel: Number(v.loyaltyEarnChannel),
        loyaltyEarnTemplate: v.loyaltyEarnTemplate.trim() || null,
        lowStockEnabled: v.lowStockEnabled,
        lowStockChannel: Number(v.lowStockChannel),
        lowStockPhone: v.lowStockPhone.trim() || null,
        lowStockTemplate: v.lowStockTemplate.trim() || null,
        smsSenderId: v.smsSenderId.trim() || null,
      });
      notify.show({ color: 'green', title: t('common.toasts.updated'), message: t('notifications.autoSaved') });
    } catch (err) {
      notify.show({
        color: 'red', title: t('common.toasts.errorTitle'),
        message: (isAxiosError(err) && (err.response?.data?.detail as string)) || t('common.toasts.errorBody'),
      });
    }
  });

  const rows = log.data ?? [];
  const smsOff = status.data && !status.data.smsConfigured;
  const waOff = status.data && !status.data.whatsAppConfigured;

  const doSend = async () => {
    if (!phone.trim() || !message.trim()) return;
    try {
      const res = await send.mutateAsync({ channel: Number(channel), phone: phone.trim(), message: message.trim() });
      notify.show({
        color: res.success ? 'green' : 'red',
        title: res.success ? t('notifications.sentOk') : t('notifications.sentFail'),
        message: res.toPhone,
      });
      if (res.success) setMessage('');
    } catch (err) {
      notify.show({
        color: 'red', title: t('common.toasts.errorTitle'),
        message: (isAxiosError(err) && (err.response?.data?.detail as string)) || t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Stack gap="lg">
      <PageHeader eyebrow={t('nav.sections.organization')} title={t('notifications.title')} subtitle={t('notifications.subtitle')} />

      {(smsOff || waOff) && (
        <Alert color="yellow" icon={<IconAlertTriangle size={18} />} variant="light">
          {smsOff && waOff ? t('notifications.bothOff') : smsOff ? t('notifications.smsOff') : t('notifications.waOff')}
        </Alert>
      )}

      <Paper p="lg">
        <form onSubmit={saveSettings}>
          <Stack gap="md">
            <Title order={5} c="var(--wheels-ink-strong)">{t('notifications.autoTitle')}</Title>
            <Switch
              label={t('notifications.orderPaidEnabled')}
              description={t('notifications.orderPaidHelp')}
              checked={settingsForm.values.orderPaidEnabled}
              onChange={(e) => settingsForm.setFieldValue('orderPaidEnabled', e.currentTarget.checked)}
            />
            {settingsForm.values.orderPaidEnabled && (
              <>
                <SegmentedControl
                  value={settingsForm.values.orderPaidChannel}
                  onChange={(v) => settingsForm.setFieldValue('orderPaidChannel', v)}
                  data={[{ value: '1', label: t('notifications.sms') }, { value: '2', label: t('notifications.whatsapp') }]}
                />
                <Textarea
                  label={t('notifications.template')}
                  description={t('notifications.templateHelp')}
                  autosize minRows={2} maxLength={1000}
                  placeholder={t('notifications.templatePlaceholder')}
                  {...settingsForm.getInputProps('orderPaidTemplate')}
                />
              </>
            )}

            <Switch
              label={t('notifications.loyaltyEnabled')}
              description={t('notifications.loyaltyHelp')}
              checked={settingsForm.values.loyaltyEarnEnabled}
              onChange={(e) => settingsForm.setFieldValue('loyaltyEarnEnabled', e.currentTarget.checked)}
            />
            {settingsForm.values.loyaltyEarnEnabled && (
              <>
                <SegmentedControl
                  value={settingsForm.values.loyaltyEarnChannel}
                  onChange={(v) => settingsForm.setFieldValue('loyaltyEarnChannel', v)}
                  data={[{ value: '1', label: t('notifications.sms') }, { value: '2', label: t('notifications.whatsapp') }]}
                />
                <Textarea
                  label={t('notifications.template')}
                  description={t('notifications.loyaltyTemplateHelp')}
                  autosize minRows={2} maxLength={1000}
                  placeholder={t('notifications.loyaltyTemplatePlaceholder')}
                  {...settingsForm.getInputProps('loyaltyEarnTemplate')}
                />
              </>
            )}

            <Switch
              label={t('notifications.lowStockEnabled')}
              description={t('notifications.lowStockHelp')}
              checked={settingsForm.values.lowStockEnabled}
              onChange={(e) => settingsForm.setFieldValue('lowStockEnabled', e.currentTarget.checked)}
            />
            {settingsForm.values.lowStockEnabled && (
              <>
                <SegmentedControl
                  value={settingsForm.values.lowStockChannel}
                  onChange={(v) => settingsForm.setFieldValue('lowStockChannel', v)}
                  data={[{ value: '1', label: t('notifications.sms') }, { value: '2', label: t('notifications.whatsapp') }]}
                />
                <TextInput
                  label={t('notifications.lowStockPhone')}
                  description={t('notifications.lowStockPhoneHelp')}
                  placeholder="0569848484"
                  className="font-numeric"
                  withAsterisk
                  {...settingsForm.getInputProps('lowStockPhone')}
                />
                <Textarea
                  label={t('notifications.template')}
                  description={t('notifications.lowStockTemplateHelp')}
                  autosize minRows={2} maxLength={1000}
                  placeholder={t('notifications.lowStockTemplatePlaceholder')}
                  {...settingsForm.getInputProps('lowStockTemplate')}
                />
              </>
            )}

            <TextInput
              label={t('notifications.senderId')}
              description={t('notifications.senderIdHelp')}
              placeholder="Wheels"
              maxLength={40}
              {...settingsForm.getInputProps('smsSenderId')}
            />

            <Group justify="flex-end">
              <Button type="submit" variant="light" loading={updateSettings.isPending}>{t('common.save')}</Button>
            </Group>
          </Stack>
        </form>
      </Paper>

      <Paper p="lg">
        <Stack gap="md">
          <Title order={5} c="var(--wheels-ink-strong)">{t('notifications.testTitle')}</Title>
          <SegmentedControl
            value={channel}
            onChange={setChannel}
            data={[
              { value: '1', label: t('notifications.sms') },
              { value: '2', label: t('notifications.whatsapp') },
            ]}
          />
          <TextInput label={t('notifications.phone')} placeholder="0569848484" className="font-numeric"
            value={phone} onChange={(e) => setPhone(e.currentTarget.value)} />
          <Textarea label={t('notifications.message')} autosize minRows={3} maxLength={2000}
            value={message} onChange={(e) => setMessage(e.currentTarget.value)} />
          <Group justify="flex-end">
            <Button leftSection={<IconSend size={16} stroke={1.8} />} onClick={doSend} loading={send.isPending}
              disabled={!phone.trim() || !message.trim()}>
              {t('notifications.send')}
            </Button>
          </Group>
        </Stack>
      </Paper>

      <div>
        <Title order={5} c="var(--wheels-ink-strong)" mb="sm">{t('notifications.logTitle')}</Title>
        <Paper p={rows.length ? 0 : 'lg'}>
          {log.isLoading ? (
            <Stack align="center" py={48}><Loader size="sm" /></Stack>
          ) : rows.length === 0 ? (
            <EmptyState icon={<IconSend size={22} stroke={1.6} />} title={t('notifications.logEmpty')} description={t('notifications.logEmptyBody')} />
          ) : (
            <Table verticalSpacing="sm" highlightOnHover striped="even">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('notifications.when')}</Table.Th>
                  <Table.Th>{t('notifications.channel')}</Table.Th>
                  <Table.Th>{t('notifications.to')}</Table.Th>
                  <Table.Th>{t('notifications.message')}</Table.Th>
                  <Table.Th>{t('common.status')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((n) => (
                  <Table.Tr key={n.notificationLogId}>
                    <Table.Td className="font-numeric" c="var(--wheels-ink-muted)">{fmtDate(n.createdAt)}</Table.Td>
                    <Table.Td>
                      {n.channel === 2
                        ? <Badge color="teal" variant="light" radius="sm" leftSection={<IconBrandWhatsapp size={12} />}>{t('notifications.whatsapp')}</Badge>
                        : <Badge color="blue" variant="light" radius="sm" leftSection={<IconMessage size={12} />}>{t('notifications.sms')}</Badge>}
                    </Table.Td>
                    <Table.Td className="font-numeric">{n.toPhone}</Table.Td>
                    <Table.Td c="var(--wheels-ink-muted)" style={{ maxWidth: 320 }}>{n.body}</Table.Td>
                    <Table.Td>
                      {n.status === 1 ? (
                        <Badge color="green" variant="light" radius="sm">{t('notifications.statusSent')}</Badge>
                      ) : n.status === 0 ? (
                        <Badge color="yellow" variant="light" radius="sm">
                          {t('notifications.statusPending')}{n.attempts > 0 ? ` (${n.attempts})` : ''}
                        </Badge>
                      ) : (
                        <Badge color="red" variant="light" radius="sm">
                          {t('notifications.statusFailed')}{n.attempts > 1 ? ` (${n.attempts})` : ''}
                        </Badge>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>
      </div>
    </Stack>
  );
}
